'use client';

import { uploadMedia } from "@/app/(frontend)/product/create/actions";
import { createReporting, updateReporting } from "@/app/actions/collections/reportings";
import { Button } from "@/ui/shadcn/button";
import { Input } from "@/ui/shadcn/input";
import { Label } from "@/ui/shadcn/label";
import { Textarea } from "@/ui/shadcn/textarea";
import { useRef, useState } from "react";
import { Media } from "@/payload-types";

export function BugReportBlock(datas) {

    const inputDescriptionRef = useRef<HTMLTextAreaElement>(null);
    const inputEmailRef = useRef<HTMLInputElement>(null);
    const inputScreenshotRef = useRef<HTMLInputElement>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({
        display: false,
        error: false,
        message: ''
    });

    const resetForm = () => {
        inputDescriptionRef.current.value = '';
        inputEmailRef.current.value = '';
        inputScreenshotRef.current.value = '';
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setMessage({
            display: false,
            error: false,
            message: ''
        });
        setIsLoading(true);
        try {
            const formData = new FormData(event.target as HTMLFormElement);
    
            const images = formData.getAll('screenshot');

            const imageIds = await Promise.all(images
                .filter((image: File) => image.size > 0)
                .map(image => uploadMedia(image as File)))
    
            if (!formData.get('bugDescription') || !formData.get('email')) {
                console.error('Missing required fields');
                return;
            }
    
            const reportingData = {
                email: formData.get('email'),
                description: formData.get('bugDescription'),
            }
    
            const reporting = await createReporting(reportingData);
    
            await updateReporting(reporting.id, imageIds);

            setIsLoading(false);
            resetForm();

            setMessage({
                display: true,
                error: false,
                message: 'Votre message a bien été envoyé, merci pour votre contribution !'
            });
            setTimeout(() => {
                setMessage({
                    display: false,
                    error: false,
                    message: ''
                });
            }, 5000);
        } catch (error) {
            console.error('Error submitting bug report:', error);
            setIsLoading(false);
            setMessage({
                display: true,
                error: true,
                message: 'Une erreur est survenue lors de l\'envoi de votre message, merci de réessayer plus tard.'
            });
        }
    } 

    return (
        <div className="container py-10">
            <h2 className="text-3xl font-bold font-alt mb-4">{datas.title}</h2>
            <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="w-full md:w-1/2">
                    <img 
                        src={(datas.image as Media)?.s3Url || '/images/bug-report.svg'} 
                        alt="Bug Report Illustration"
                        className="w-full h-auto object-cover rounded-lg"
                    />
                </div>
                <div className="w-full md:w-1/2">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="bugDescription" className="mb-2">
                                {datas.inputDescriptionLabel}
                            </Label>
                            <Textarea
                                id="bugDescription"
                                name="bugDescription"
                                required
                                ref={inputDescriptionRef}
                            />
                        </div>
                        <div>
                            <Label htmlFor="email" className="mb-2">
                                {datas.inputEmailLabel}
                            </Label>
                            <Input
                                type="email"
                                id="email"
                                name="email"
                                required
                                ref={inputEmailRef}
                            />
                        </div>
                        <div>
                            <Label htmlFor="screenshot" className="mb-2">
                                {datas.inputScreenshotLabel}
                            </Label>
                            <Input
                                type="file"
                                id="screenshot"
                                name="screenshot"
                                accept="image/*"
                                multiple
                                ref={inputScreenshotRef}
                            />
                        </div>
                        <Button
                            type="submit"
                            variant="default"
                            size="lg"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Envoi...' : datas.submitButtonLabel}
                        </Button>
                    </form>
                    {message.display && (
                        <div className={`${message.error ? 'text-red-500' : 'text-green-500'} mt-4`}>
                            {message.message}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
